import { FC, useCallback, useEffect, useRef, useState } from "react";
import ShopLayout1 from "components/layouts/ShopLayout1";
import Navbar from "components/navbar/Navbar";
import BuildsGalleryHeader from "components/builds-gallery/components/BuildGalleryHeader";
import { Theme } from "@mui/material/styles";
import { makeStyles } from "@mui/styles";
import { colors } from "common";
import {
  Box,
  Card,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Pagination,
  Select,
  Typography,
} from "@mui/material";
import { BuildGalleryListItem } from "components/builds-gallery/components/BuildGalleryListItem";
import modules from "modules";
import { unwrapResult } from "@reduxjs/toolkit";
import { useAppDispatch } from "hooks/use-app-dispatch";
import Link from "next/link";
import { useBuildsGallery } from "hooks/use-builds-gallery";
import { useRouter } from "next/router";
import BuildFilterCard from "components/builds-gallery/components/BuildFilterCard";
import { H4 } from "components/Typography";
import { sortDirectionVal } from "common/constants/products";
import { BuildsFilterDrawer } from "../../src/components/builds-gallery/components/BuildsFilterDrawer";
import useResolution from "hooks/useResolution";

// ===============================================================
type Props = {};
// ===============================================================

const useStyles = makeStyles((theme: Theme) => ({
  root: {},
  listContainer: {
    marginLeft: theme.spacing(7),
    marginRight: theme.spacing(7),
    marginTop: theme.spacing(4),
    paddingBottom: theme.spacing(20),
    [theme.breakpoints.down("md")]: {
      marginLeft: theme.spacing(4),
      marginRight: theme.spacing(4),
    },
  },
  disclaimerRoot: {
    marginTop: theme.spacing(2),
  },
  disclaimerContainer: {
    fontWeight: 500,
    letterSpacing: 0.75,
    fontSize: 10,
    color: colors.grey[400],
  },
  paginationContainer: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: theme.spacing(2),
  },
}));

const limitPerPage = 50;

const initialRequestParams = {
  keyword: "",
  sort: {
    field: "created_at",
    direction: "desc",
    amount: "",
  },
  filter: {
    cpu: "",
    build_tier: "",
    price_range: "",
  },
  page: 1,
  limit: limitPerPage,
};

const { actions: BuildsGalleryActions } = modules.buildsGallery;

const BuildsGalleryPage: FC<Props> = ({}) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { buildsSearchProductName } = useBuildsGallery();
  const { query } = useRouter();
  const router = useRouter();
  const { isMobile, isIPadMini } = useResolution();

  const [buildsItem, getBuildsItem] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [requestBody, setRequestBody] = useState(initialRequestParams);
  const [clearTriggered, setClearTriggered] = useState(false);
  const [searchInputVal, setSearchInputVal] = useState<string>(
    (query.keyword as string) || ""
  );
  const [toggleShowFilter, setFilterOpen] = useState(false);
  const [paginationMeta, setPaginationMeta] = useState<any>();
  const [sortDirection, setSortDirection] = useState<string | undefined>(
    undefined
  );
  const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth);
  const [clearFilters, setClearFilters] = useState(false);

  useEffect(() => {
    if (router.query.sort) {
      const sortObject = JSON.parse(router.query.sort as string);
      const decodedDirection = decodeURIComponent(sortObject.direction);

      if (decodedDirection === "asc") {
        setSortDirection("Lowest to Highest");
      } else if (decodedDirection === "desc") {
        setSortDirection("Highest to Lowest");
      } else {
        setSortDirection("");
      }
    }
  }, [router.query.sort, sortDirection]);

  const getBuildsGalleryItem = useCallback(
    async (requestBodyArg = initialRequestParams) => {
      setIsLoading(true);
      const response = unwrapResult(
        await dispatch(
          BuildsGalleryActions.getBuildsGalleryPageThunk(
            requestBodyArg || { limit: 50, sort: "DESC" }
          )
        )
      );

      if (response?.success) {
        if (response?.originalData?.meta) {
          setPaginationMeta(response?.originalData?.meta);
        }
        setIsLoading(false);
      }
      getBuildsItem(response?.originalData?.data);
    },
    [dispatch]
  );

  useEffect(() => {
    if (Object.keys(query).length === 0) return;

    const updatedFilters = {
      keyword: query.keyword || "",
      filter: {
        cpu: Array.isArray(query.cpu) ? query.cpu[0] : query.cpu || "",
        build_tier: Array.isArray(query.build_tier)
          ? query.build_tier[0]
          : query.build_tier || "",
        price_range: Array.isArray(query.price_range)
          ? query.price_range[0]
          : query.price_range || "",
      },
      sort: query.sort
        ? JSON.parse(Array.isArray(query.sort) ? query.sort[0] : query.sort)
        : undefined,
      page: Number(query.page) || 1,
    };

    setSearchInputVal(
      Array.isArray(updatedFilters.keyword)
        ? updatedFilters.keyword[0]
        : updatedFilters.keyword
    );
    setSortDirection(updatedFilters.sort?.direction || "");
    setRequestBody((prev) => ({
      ...prev,
      ...updatedFilters,
      keyword: Array.isArray(updatedFilters.keyword)
        ? updatedFilters.keyword[0]
        : updatedFilters.keyword,
    }));
    getBuildsGalleryItem(updatedFilters);
    setTimeout(() => getBuildsGalleryItem(updatedFilters), 0);
  }, [getBuildsGalleryItem, query]);

  const updateUrl = (updatedParams) => {
    router.push({
      pathname: router.pathname,
      query: {
        ...query,
        ...updatedParams,
      },
    });
  };

  const searchBuildsGalleryList = async () => {
    if (!searchInputVal.trim()) return;

    const updatedParams = {
      ...requestBody,
      keyword: searchInputVal,
      page: 1,
    };

    setRequestBody(updatedParams);
    getBuildsGalleryItem(updatedParams);
    updateUrl({ keyword: searchInputVal, page: 1 });
  };

  const onChangeSearchInput = (e) => {
    const newValue = e.target.value;
    setSearchInputVal(newValue);

    if (!newValue.trim()) {
      setRequestBody(initialRequestParams);
      getBuildsGalleryItem(initialRequestParams);
      updateUrl({ keyword: undefined });
    }
  };

  const onChangePriceRange = (price_range) => {
    const updatedParams = {
      ...requestBody,
      filter: { ...requestBody.filter, price_range },
    };

    setRequestBody(updatedParams);
    getBuildsGalleryItem(updatedParams);
    updateUrl({ price_range });
  };

  const onCategoryChange = (filterValue) => {
    if (!filterValue) return;

    const isCPU = ["AMD", "Intel"].includes(filterValue);
    const isBuildTier = ["Entry", "Mid", "Premium"].includes(filterValue);

    const newFilter = {
      cpu: isCPU ? (query.cpu === filterValue ? "" : filterValue) : query.cpu,
      build_tier: isBuildTier
        ? query.build_tier === filterValue
          ? ""
          : filterValue
        : query.build_tier,
      price_range: "",
    };

    const newParams = {
      filter: newFilter,
      page: 1,
    };

    setRequestBody((prev) => ({ ...prev, ...newParams }));
    getBuildsGalleryItem(newParams);
    updateUrl({
      cpu: newFilter.cpu || undefined,
      build_tier: newFilter.build_tier || undefined,
      page: 1,
    });
  };

  const onChangeSortPrice = (sortField, sortDirection) => {
    if (["Highest to Lowest", "Lowest to Highest"].includes(sortDirection)) {
      setSortDirection(sortDirection);
    }
    let newParams = {
      ...requestBody,
      sort: {
        field: sortField,
        direction: sortDirection,
        amount: "",
      },
    };
    setRequestBody(newParams);
    getBuildsGalleryItem(newParams);
    updateUrl({
      sort: JSON.stringify({
        field: sortField,
        direction: sortDirection,
      }),
      page: 1,
    });
    setSearchInputVal("");
  };

  const onHandleClearKeyword = () => {
    setSearchInputVal("");
    setRequestBody(initialRequestParams);
    setSortDirection("");
    setClearFilters(true);

    setTimeout(() => setClearFilters(false), 0);

    getBuildsGalleryItem(initialRequestParams);
    router.replace(
      {
        pathname: "/builds-gallery",
        query: {},
      },
      undefined,
      { shallow: true }
    );
  };

  useEffect(() => {
    if (query.keyword) {
      setSearchInputVal(
        Array.isArray(query.keyword) ? query.keyword[0] : query.keyword
      );
    }
  }, [query.keyword]);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const updatePage = (page) => {
    const updatedParams = { ...requestBody, page, keyword: searchInputVal };

    setRequestBody(updatedParams);
    getBuildsGalleryItem(updatedParams);
    updateUrl({ page });
  };

  useEffect(() => {
    getBuildsGalleryItem(requestBody);
  }, [getBuildsGalleryItem, requestBody]);

  return (
    <ShopLayout1 title="Builds Gallery" hasSearch={false} navbar={<Navbar />}>
      <Container>
        <BuildsGalleryHeader
          isLoading={isLoading}
          onChangeSearchInput={onChangeSearchInput}
          searchInputValue={searchInputVal}
          productSearchName={buildsSearchProductName}
          searchBuildsGalleryList={searchBuildsGalleryList}
          toggleShowFilter={() => setFilterOpen(true)}
          onHandleClearKeyword={onHandleClearKeyword}
        />

        <Grid container>
          {windowWidth >= 1200 && (
            <Grid item md={3} sm={3} lg={3} sx={{ display: "block" }}>
              <Card style={{ marginTop: "2rem" }}>
                <Box sx={{ width: "100%", padding: "1rem", mt: "1rem" }}>
                  <H4 mb={"1rem"}>Filters</H4>
                  <FormControl sx={{ justifyContent: "center", width: "100%" }}>
                    <InputLabel id="sort-direction">Sort By Price</InputLabel>
                    <Select
                      sx={{
                        height: "45px",
                        textAlign: { xs: "left" },
                      }}
                      labelId="sort-direction"
                      value={
                        router.query.sort
                          ? JSON.parse(router.query.sort as string)
                              .direction === "desc"
                            ? "Highest to Lowest"
                            : "Lowest to Highest"
                          : ""
                      }
                      onChange={(e) =>
                        onChangeSortPrice(
                          "amount",
                          e.target.value === "Highest to Lowest"
                            ? "desc"
                            : "asc"
                        )
                      }
                      input={
                        <OutlinedInput
                          label="sort-direction"
                          sx={{ height: 40 }}
                        />
                      }
                    >
                      {sortDirectionVal.map((sortDir, index) => (
                        <MenuItem key={index} value={sortDir}>
                          {sortDir}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <BuildFilterCard
                  onchange={onCategoryChange}
                  onChangePriceRange={onChangePriceRange}
                  onHandleClearKeyword={clearFilters}
                />
              </Card>
            </Grid>
          )}

          <Grid item xs={12} sm={9} md={9} lg={9}>
            <div className={classes.listContainer}>
              <Grid
                container
                spacing={2}
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(auto-fill, minmax(250px, 1fr))",
                    sm: "repeat(auto-fill, minmax(250px, 1fr))",
                    md: "repeat(auto-fill, minmax(250px, 1fr))",
                    lg: "repeat(auto-fill, minmax(250px, 1fr))",
                  },
                  gap: { xs: "16px", sm: "20px" },
                  width: "100%",
                  justifyContent: "start",
                }}
              >
                {(buildsItem || []).map((gal) => (
                  <Grid
                    item
                    key={gal.uuid}
                    sx={{
                      display: "flex",
                      justifyContent: isMobile || isIPadMini ? "center" : null,
                    }}
                  >
                    <Link
                      key={`${gal.uuid}`}
                      passHref
                      href={{ pathname: `/builds-gallery/details/${gal.uuid}` }}
                    >
                      <div style={{ width: "100%", maxWidth: "250px" }}>
                        <BuildGalleryListItem data={gal} onClick={undefined} />
                      </div>
                    </Link>
                  </Grid>
                ))}
              </Grid>

              <div className={classes.disclaimerRoot}>
                <Typography className={classes.disclaimerContainer}>
                  PRICE AND STOCK(S) ARE SUBJECT TO CHANGE WITHOUT ANY PRIOR
                  NOTICE AND SHIPPING FEES NOT YET INCLUDED
                </Typography>
              </div>

              {paginationMeta?.total > limitPerPage ? (
                <div className={classes.paginationContainer}>
                  <Pagination
                    count={paginationMeta.last_page || 1}
                    page={paginationMeta.current_page || 1}
                    onChange={(e, p) => updatePage(p)}
                    onClick={() =>
                      window.scrollTo({ top: 0, behavior: "smooth" })
                    }
                  />
                </div>
              ) : null}
            </div>
          </Grid>
        </Grid>
        <BuildsFilterDrawer
          open={toggleShowFilter}
          onClose={() => setFilterOpen(false)}
          sortDirection={sortDirection}
          onChangeSortPrice={onChangeSortPrice}
          onCategoryChange={onCategoryChange}
          onChangePriceRange={onChangePriceRange}
          onHandleClearKeyword={onHandleClearKeyword}
        />
      </Container>
    </ShopLayout1>
  );
};

export default BuildsGalleryPage;
